/* eslint-disable @typescript-eslint/no-magic-numbers */
import { PopoverPlacement, Tooltip, TooltipProps } from "@idui/react-popover";
import { FC, ReactNode, useMemo } from "react";
import styled from "styled-components";

import { useRtl } from "@ryde/redux";
import { NO_VALUE_DISPLAY, useIsMobile } from "@ryde/shared";
import { greyMui } from "@ryde/theme";

interface IFramerTooltipAttributes extends Omit<
  Partial<TooltipProps>,
  "title"
> {
  tooltipDisabled?: boolean;
  rtlStrippedBottomRightPlacement?: boolean;
  rtlStrippedBottomLeftPlacement?: boolean;
  /** Opens on the inline-start side: left in LTR, right in RTL. */
  rtlStrippedLeftPlacement?: boolean;
}

const FramerTooltipStyled = styled(Tooltip).attrs<IFramerTooltipAttributes>(
  props => ({
    arrowColor: props.arrowColor || "black",
    "data-e2e": "framer-tooltip",
    closeOnRemoteClick: true,
    closeOnScroll: true,
    guessBetterPosition: props.guessBetterPosition ?? true,
    considerTriggerMotion: props.considerTriggerMotion ?? true,
    mouseEnterDelay: props.mouseEnterDelay ?? 550,
    mouseLeaveDelay: props.mouseLeaveDelay ?? 50,
  }),
)`
  background-color: black;
  color: white;
  padding: 0px;

  .idui-popover__content {
    overflow: auto;
    padding: 6px;

    /* webkit-specific */
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      background-color: ${greyMui._900};
    }
    &::-webkit-scrollbar-thumb {
      box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
      border-radius: 4px;
      background-color: ${greyMui._400};
    }
    &::-webkit-scrollbar-thumb:hover {
      background: ${greyMui._400};
    }
    @supports (-moz-appearance: none) {
      /* mozilla-specific, shouldn't be applied to Chrome */
      scrollbar-width: thin;
    }
  }
`;

export const FramerTooltip: FC<IFramerTooltipAttributes> = ({
  children,
  tooltipDisabled,
  placement,
  content,
  rtlStrippedBottomRightPlacement,
  rtlStrippedBottomLeftPlacement,
  rtlStrippedLeftPlacement,
  ...props
}) => {
  const { isMobile } = useIsMobile();
  const { isRtl } = useRtl();

  const placementComputed = useMemo(() => {
    if (rtlStrippedBottomLeftPlacement) {
      return isRtl ? PopoverPlacement.bottomRight : PopoverPlacement.bottomLeft;
    }

    if (rtlStrippedBottomRightPlacement) {
      return isRtl ? PopoverPlacement.bottomLeft : PopoverPlacement.bottomRight;
    }

    if (rtlStrippedLeftPlacement) {
      return isRtl ? PopoverPlacement.right : PopoverPlacement.left;
    }

    return placement;
  }, [
    isRtl,
    placement,
    rtlStrippedBottomLeftPlacement,
    rtlStrippedBottomRightPlacement,
    rtlStrippedLeftPlacement,
  ]);

  if (tooltipDisabled || isMobile || !content || content === NO_VALUE_DISPLAY) {
    return <>{children as ReactNode}</>;
  }

  return (
    <FramerTooltipStyled
      {...props}
      placement={placementComputed}
      content={content}
      title=""
    >
      {children}
    </FramerTooltipStyled>
  );
};
